import * as Sentry from '@sentry/browser';
import { httpsCallable } from 'firebase/functions';
import type { RecordStripePaymentPayload, RecordStripePaymentResponse } from '@usacsmideo/types';
import { functions } from '../../constants/firebaseModular';
import { dedupeSafeMessage } from '../../utils/mfa/sentryDedupe';
import { browserReportsOnline } from '../../utils/mfa/networkError';

/**
 * Ask the server to record a card payment (CU-17tm466h63h).
 *
 * The thanks page used to mark the patient paid itself, when its URL contained
 * "succeeded", with the amount taken from the query string. Now it sends only
 * Stripe's `payment_intent` id; the server asks Stripe whether it succeeded,
 * checks it belongs to this patient, and writes PAYMENT_INFO. The client never
 * writes the paid flag.
 *
 * Throws a typed `RecordPaymentError`; the caller owns the UI.
 */

/** Generous on purpose: the function is not kept warm, and cold starts here have measured 19.9–28.2 s. */
export const RECORD_PAYMENT_TIMEOUT_MS = 60_000;

export type RecordPaymentErrorKind =
  /** Server or network failure. Retrying is safe: the server write is idempotent. */
  | 'retryable'
  /** The browser reports it is offline. */
  | 'offline'
  /** Too many attempts in a minute. */
  | 'rate-limited'
  /** Malformed or unknown intent id, e.g. a hand-typed URL. */
  | 'not-found'
  /** The intent belongs to another account (e.g. an admin viewing as a patient). */
  | 'refused'
  /** The intent is real but carries no patient binding; staff must reconcile. */
  | 'unconfirmable';

export class RecordPaymentError extends Error {
  readonly kind: RecordPaymentErrorKind;
  readonly code?: string;

  constructor(kind: RecordPaymentErrorKind, message: string, code?: string) {
    super(message);
    this.name = 'RecordPaymentError';
    this.kind = kind;
    this.code = code;
  }
}

function classify(code: string | undefined): RecordPaymentErrorKind {
  switch (code) {
    case 'functions/invalid-argument':
      return 'not-found';
    case 'functions/permission-denied':
      return 'refused';
    case 'functions/failed-precondition':
      return 'unconfirmable';
    case 'functions/resource-exhausted':
      return 'rate-limited';
    default:
      return 'retryable';
  }
}

/** Reported: a failure staff or engineering must see. Not reported: a typed URL, a refusal, a rate limit. */
const REPORTED: ReadonlySet<RecordPaymentErrorKind> = new Set(['retryable', 'unconfirmable']);

/**
 * ⚠️ No offline check here, on purpose. Any failure while offline is already
 * classified `offline`, which is not in REPORTED, so a check here was dead code
 * (adversarial review F8, mutant A17). Offline failures are therefore NEVER
 * reported, not even after reconnecting; there is no equivalent of
 * utils/mfa/offlineFailureReport.ts on this path.
 */
function report(error: RecordPaymentError, paymentIntentId: string): void {
  Sentry.captureException(new Error(dedupeSafeMessage(`recordStripePayment failed: ${error.kind}`)), {
    fingerprint: ['record-stripe-payment', error.kind],
    tags: { record_payment_error: error.kind },
    // The intent id is not PHI and is what support needs to reconcile.
    extra: { code: error.code, paymentIntentId },
  });
}

export async function recordStripePayment(paymentIntentId: string): Promise<RecordStripePaymentResponse> {
  const call = httpsCallable<RecordStripePaymentPayload, RecordStripePaymentResponse>(functions, 'recordStripePayment', {
    timeout: RECORD_PAYMENT_TIMEOUT_MS,
  });
  try {
    const { data } = await call({ paymentIntentId });
    return data;
  } catch (err) {
    const code = (err as { code?: string })?.code;
    // `functions/internal` is offline OR a server 500; only the browser can tell them apart.
    const kind: RecordPaymentErrorKind = !browserReportsOnline() ? 'offline' : classify(code);
    const error = new RecordPaymentError(kind, (err as Error)?.message ?? String(err), code);
    if (REPORTED.has(kind)) report(error, paymentIntentId);
    throw error;
  }
}
